import React, { useState } from "react";
import styled from "styled-components";
import { Button } from "../../../../../design-system";
import { retryScheduledPost, ScheduledPost } from "../../../../../database/community/scheduledPosts";
import { platformLabel } from "../../publish/SchedulePostDialog";
import { useMediaStudio } from "../../context/MediaStudioContext";
import { Muted, Row, RowList, Spacer, StatusDot } from "../shared";
import Modal from "../Modal";
import { timeLabel } from "./homeDerive";

const Strip = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
`;

const Item = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  background: none;
  border: none;
  padding: 2px 4px;
  border-radius: var(--radius-sm);
  cursor: pointer;

  &:hover {
    background: var(--color-surface-subtle);
  }
`;

const ErrorText = styled.span`
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-danger);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export type ConnectionsStripProps = {
  onNavigate: (path: string) => void;
};

// Attention-only strip: connecting platforms is a one-time setup, so once
// everything is connected and nothing failed there is nothing to show and
// the strip renders null. It appears only with unconnected platforms (setup
// nudge) or failed posts (the counter opens a modal with the errors).
export const ConnectionsStrip: React.FC<ConnectionsStripProps> = ({
  onNavigate,
}) => {
  const { communityId, connections, posts } = useMediaStudio();
  const [showFailed, setShowFailed] = useState(false);

  const rows: { label: string; connected: boolean }[] = [
    { label: "TikTok", connected: connections.flags.tiktok },
    { label: "LinkedIn", connected: connections.flags.linkedin },
    { label: "Instagram", connected: connections.flags.instagram },
    { label: "Facebook", connected: connections.flags.facebook },
    { label: "YouTube", connected: connections.flags.youtube },
  ];
  const missing = rows.filter((r) => !r.connected);
  const failed = posts.errors;

  if (connections.loading) return null;
  if (missing.length === 0 && failed.length === 0) return null;

  const errorLine = (p: ScheduledPost): string => {
    if (!p.results) return "";
    const parts = Object.entries(p.results)
      .filter(([, r]) => r?.error)
      .map(([platform, r]) => `${platformLabel(platform)}: ${r.error}`);
    return parts.join(" · ");
  };

  return (
    <>
      <Strip>
        {missing.map((r) => (
          <Item
            key={r.label}
            type="button"
            title="Not connected. Open settings"
            onClick={() => onNavigate("/media-studio/settings?section=connections")}
          >
            <StatusDot $color="var(--color-text-tertiary)" />
            {r.label}
          </Item>
        ))}
        {missing.length > 0 && (
          <Muted>
            {missing.length === 1
              ? "1 platform not connected yet"
              : `${missing.length} platforms not connected yet`}
          </Muted>
        )}
        {failed.length > 0 && (
          <Item type="button" onClick={() => setShowFailed(true)}>
            <StatusDot $color="var(--color-danger)" />
            <Muted style={{ color: "var(--color-danger)" }}>
              {failed.length} post{failed.length === 1 ? "" : "s"} failed
            </Muted>
            <Muted>details</Muted>
          </Item>
        )}
        <Spacer />
        {missing.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onNavigate("/media-studio/settings?section=connections")}
          >
            Connect
          </Button>
        )}
      </Strip>

      {showFailed && failed.length > 0 && (
        <Modal title="Failed posts" onClose={() => setShowFailed(false)}>
          <RowList>
            {failed.map((p) => (
              <Row key={p.id}>
                <StatusDot $color="var(--color-danger)" />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontFamily: "var(--font-sans)",
                      fontSize: "var(--font-size-sm)",
                      color: "var(--color-text)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.caption?.trim() || "Untitled post"}
                  </div>
                  <Muted>
                    {timeLabel(p.dueAt)} · {p.platforms.map(platformLabel).join(", ")}
                  </Muted>
                  {errorLine(p) && <ErrorText>{errorLine(p)}</ErrorText>}
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => communityId && retryScheduledPost(communityId, p.id)}
                >
                  Retry
                </Button>
              </Row>
            ))}
          </RowList>
        </Modal>
      )}
    </>
  );
};

export default ConnectionsStrip;
